import React, {useState} from "react"
import {omit} from "lodash"
import GenericModal from "@irati/ui/src/GenericModal"
import Scheduler from "@irati-web/recognition/src/components/Popup/CreateRecognition/Scheduler"
import {Row, Col} from "@irati/ui/src/ColumnRow"
import Spacing from "@irati/ui/src/Spacing"
import {ListScheduledRewards, ScheduledTime} from "@irati/types"
import {useModifyBulkReward} from "@irati/wallet/hooks/rewards"
import IntlMarkdown from "../../styled/IntlMarkdown"
import FormattedErrorText from "../../styled/FormattedErrorText"

interface Props {
  show: boolean
  onHide(): void
  reward: ListScheduledRewards
  onComplete(scheduledNow: boolean): void
}

export function EditModal({onHide, onComplete, reward, show}: Props) {
  const [isScheduledNow, setIsScheduledNow] = useState(false)
  const [newSchedule, setNewSchedule] = useState<ScheduledTime>(reward?.scheduledFor)
  const [inputError, setInputError] = useState("")
  const [modifyReward, {load}] = useModifyBulkReward({})

  return (
    <GenericModal
      proceedButtonText="wallet.statementItem.adminReward.scheduledReward.editPopup.proceed"
      dismissButtonText="platform.button.cancel"
      show={show}
      load={load}
      onConfirm={() => {
        if (!isScheduledNow && datesEqual(reward.scheduledFor, newSchedule)) {
          return setInputError("wallet.statementItem.adminReward.scheduledReward.editPopup.unchanged")
        }

        modifyReward({
          variables: {
            input: {
              ...omit(reward, ["currency", "recipientCount", "scheduledFor", "destinationIds", "createdAt"]),
              schedule: {
                when: isScheduledNow ? new Date().toISOString() : newSchedule.when,
                originalTimezone: newSchedule.originalTimezone
              }
            }
          }
        })
          .then(() => onComplete(isScheduledNow))
          .catch(() => {})
      }}
      onHide={onHide}>
      <IntlMarkdown id="wallet.statementItem.adminReward.scheduledReward.editPopup.body" />
      <Spacing marginBottom={4} />
      {reward && (
        <Row marginBottom={4}>
          <Col xs={12}>
            <Scheduler
              openBelow
              defaultValue={{
                timezone: newSchedule?.originalTimezone ?? reward.scheduledFor.originalTimezone,
                date: newSchedule?.when ?? reward.scheduledFor.when
              }}
              onChange={(utc, timezone) => {
                setInputError("")
                setNewSchedule({when: utc, originalTimezone: timezone})
              }}
              onScheduleOptionChange={(isScheduled) => {
                setInputError("")
                setIsScheduledNow(!isScheduled)
              }}
            />
          </Col>
        </Row>
      )}
      {inputError && <FormattedErrorText error={inputError} />}
    </GenericModal>
  )
}

function datesEqual(old: ScheduledTime, updated: ScheduledTime) {
  return updated.originalTimezone === old.originalTimezone && new Date(updated.when).toISOString() === new Date(old.when).toISOString()
}
